import { useEffect, useMemo, useState } from "react";
import { PartConfiguratorFormApi } from "src/api/generated/erp/configurator/api/partConfiguratorFormApi.ts";
import {
	OnComponentInputChangedParams,
	OnComponentInputChangedResult,
} from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorForm.tsx";
import {
	addChildToTreeItem,
	getSubConfiguratorListIndexChangedChildKeys,
	regenerateKeysForAllTreeItems,
	updateComponentValues,
	updateConfiguratorTreeItemState,
	updateSubConfiguratorListIndexes,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemMutators.ts";
import {
	findTreeItem,
	findTreeItemParent,
	getAnyAncestorHasErrors,
	getConfiguratorTreeRecursiveInputErrors,
	getTreeItemConfiguratorInput,
	getTreeItemRecursiveConfiguratorInput,
	getTreeItemSubConfiguratorComponents,
	getTreeItemWithParentProperties,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	AddSubConfiguratorsToTreeView,
	AddSubConfiguratorsToTreeViewProps,
	SubConfigurationImportButtonAction,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/AddSubConfiguratorsToTreeView.tsx";
import i18n from "i18next";
import { FormResult } from "src/components/common/forms/types";
import { ConfigurationPropertyValues } from "src/api/generated/erp/configurator/model/configurationPropertyValues.ts";
import {
	PartConfiguratorTreeStatelessView,
	PartConfiguratorTreeStatelessViewProps,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatelessView.tsx";
import { ConfiguratorTreeErrorsView } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/ConfiguratorTreeErrorsView.tsx";
import {
	TransformedConfigurationComponent_SubConfigurator,
	TransformedConfigurationComponent_SubConfiguratorList,
} from "src/api/generated/erp/configurator/componentTransformation/model/transformedConfigurationComponent.ts";
import { getConfiguratorFormErrors } from "src/components/views/erp/configurator/configuratorForm/validation.ts";
import { ConfiguratorEvent } from "src/api/generated/erp/configurator/model/configuratorEvent.ts";
import { useStateWithRef } from "src/utils/useStateWithRef.tsx";
import {
	clearStoredConfiguratorTreeModel,
	storeConfiguratorTreeModelToServer,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/configuratorTreeModelStorage.ts";
import { ConfiguratorTreeModelSource } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeModelSource.ts";
import { logError, LoggedError } from "src/errorHandling/errorLogging.ts";
import { PropertyInputValues } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel";
import { ConfiguratorTreeModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeModel.ts";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import {
	ConfiguratorInput,
	ConfiguratorInput_InputValue_Scalar,
} from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { ConfiguratorTreeItemInputErrors } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/ConfiguratorTreeItemInputErrors.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { Typography } from "@mui/material";
import { SubConfiguratorPartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/impl/subConfiguratorPartConfiguratorType.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { ConfigurationCheckpointProvider } from "src/components/views/erp/configurator/configuratorForm/checkpoints/ConfigurationCheckpointProvider.tsx";
import {
	ConfigurationCheckpointState,
	defaultCheckpointState,
} from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";
import {
	prepareImportedSubConfiguration,
	SubConfigurationImportAssignment,
	synchronizeImportedSubConfigurationDescendantParentTypes,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/subConfigurationImportUtils.ts";

export interface SubConfigurationImportAction {
	title: string;
	onClick: (params: {
		targetParent: ConfiguratorTreeItemModel;
		importSubConfigurations: (assignments: SubConfigurationImportAssignment[]) => Promise<void>;
	}) => Promise<void>;
}

export interface PartConfiguratorTreeStatefulViewProps
	extends
		Pick<
			PartConfiguratorTreeStatelessViewProps,
			| "configuratorType"
			| "hideFooter"
			| "footerExtraComponents"
			| "readonly"
			| "serverErrorCustomContent"
			| "validationEnabled"
			| "configuratorComponentSx"
			| "configuratorComponentContextMenu"
		>,
		Pick<AddSubConfiguratorsToTreeViewProps, "addSubConfiguratorButtonSx" | "addSubConfiguratorButtonContextMenu"> {
	initialTreeModel: ConfiguratorTreeModel;
	treeModelStorageKey: string | undefined;
	hasSavedPartConfiguration: boolean;
	// Called on cancel and before resolving the final property values.
	onSubmit?: (result: FormResult<ConfiguratorInput>) => Promise<unknown>;
	// Called on cancel and after resolving the final property values.
	// Return PART_CONFIGURATION_NOT_SAVED when a successful result was not saved, e.g. because the user cancelled a
	// follow-up dialog. The configurator then keeps its unsaved changes.
	onCompleted?: (result: FormResult<PartConfiguratorTreeResult>) => Promise<unknown>;
	allowInputErrors?: boolean;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
	subConfigurationImportAction?: SubConfigurationImportAction;
}

export const PART_CONFIGURATION_NOT_SAVED = "notSaved";

export interface PartConfiguratorTreeResult {
	/** The values that the component form resolved. */
	propertyValues: ConfigurationPropertyValues | undefined;
	/** The input of a property-model form, which the server validates and resolves when it is saved. */
	propertyInputValues?: PropertyInputValues;
	configuratorInput: ConfiguratorInput;
	configurationSessionId: string;
	checkpointState: ConfigurationCheckpointState;
}

export const PartConfiguratorTreeStatefulView = ({
	configuratorType,
	initialTreeModel,
	hideFooter,
	footerExtraComponents,
	onSubmit,
	onCompleted,
	serverErrorCustomContent,
	readonly,
	treeModelStorageKey,
	hasSavedPartConfiguration,
	validationEnabled,
	configuratorComponentSx,
	configuratorComponentContextMenu,
	addSubConfiguratorButtonSx,
	addSubConfiguratorButtonContextMenu,
	allowInputErrors = false,
	onDataDirtyStateChanged,
	subConfigurationImportAction,
}: PartConfiguratorTreeStatefulViewProps) => {
	// Use rootItemRenderState for rendering, rootItemRef for procedural code (API-calls, chained updates)
	const [rootItemRenderState, rootItemRef, setRootItem] = useStateWithRef<ConfiguratorTreeItemModel>(
		initialTreeModel.root,
	);

	const [isLoading, setIsLoading] = useState(false);
	const [serverError, setServerError] = useState<LoggedError | undefined>(undefined);
	const [configuratorInputVersion, setConfiguratorInputVersion] = useState<number>(-1);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(initialTreeModel.hasUnsavedChanges);

	const [selectedItemKey, setSelectedItemKey] = useState<string>(rootItemRenderState.key);
	const selectedTreeItem = useMemo(
		() => findTreeItem(rootItemRenderState, selectedItemKey),
		[rootItemRenderState, selectedItemKey],
	);

	const [checkpointState, setCheckpointState] = useState<ConfigurationCheckpointState>(defaultCheckpointState);

	const { openDialog } = useGenericDialog();

	const showMessageDialog = useMessageDialog();
	const { environmentType } = useGlobalInitData();

	useEffect(() => {
		onDataDirtyStateChanged?.({
			isDirty: hasUnsavedChanges,
			confirmCloseWhileDirtyMessage: i18n.t("confirm_close_configurator_without_saving"),
		});
	}, [hasUnsavedChanges, onDataDirtyStateChanged]);

	return (
		<ConfigurationCheckpointProvider
			configurationSessionId={rootItemRenderState.configurationSessionId}
			configuratorType={rootItemRenderState.configuratorType}
			checkpointState={checkpointState}
			setCheckpointState={setCheckpointState}
		>
			<PartConfiguratorTreeStatelessView
				configuratorType={configuratorType}
				rootItem={rootItemRenderState}
				selectedItem={selectedTreeItem}
				selectTreeItem={selectTreeItem}
				toggleTreeItemExpansion={toggleTreeItemExpansion}
				onAddSubConfiguratorClicked={onAddSubConfiguratorClicked}
				deleteSubConfiguratorListItem={deleteSubConfiguratorListItem}
				cloneSubConfiguratorListItem={cloneSubConfiguratorListItem}
				onComponentInputChanged={(key, params) => onComponentInputChanged(key, params)}
				transformTreeItemComponents={(treeItemKey) => transformTreeItemComponents({ treeItemKey: treeItemKey })}
				resetToDefaultValues={() => resetForm("SCRATCH")}
				resetToExistingConfiguration={
					hasSavedPartConfiguration ? () => resetForm("SAVED_CONFIGURATION") : undefined
				}
				updateVersions={updateAllVersions}
				updateSingleVersion={updateSingleSubConfiguratorVersion}
				configuratorInputVersion={configuratorInputVersion}
				isLoading={isLoading}
				hasUnsavedChanges={hasUnsavedChanges}
				readonly={readonly}
				allowInputErrors={allowInputErrors}
				onSubmit={onSubmitForm}
				onCancel={onCancelForm}
				onValidate={onValidateConfiguration}
				hideFooter={hideFooter}
				footerExtraComponents={footerExtraComponents}
				serverError={serverError}
				serverErrorCustomContent={serverErrorCustomContent}
				configuratorComponentSx={configuratorComponentSx}
				configuratorComponentContextMenu={configuratorComponentContextMenu}
				validationEnabled={validationEnabled}
			/>
		</ConfigurationCheckpointProvider>
	);

	async function onComponentInputChanged(
		key: string,
		{ component, newValue }: OnComponentInputChangedParams,
	): Promise<OnComponentInputChangedResult> {
		updateConfiguratorInput(key, {
			[component.name]: newValue,
		});

		if (component.type === "field" && component.refreshOnChange) {
			await transformTreeItemComponents({
				treeItemKey: key,
				recursive: component.refreshOnChangeRecursively,
				event: {
					type: "componentValueChanged",
					componentId: component.configurationComponentId,
				},
			});
		}

		return {
			newConfiguratorInput: getTreeItemConfiguratorInput(findTreeItem(rootItemRef.current, key)!),
		};
	}

	function updateConfiguratorInput(
		treeItemKey: string,
		updatedComponentValuesByNames: Record<string, ConfiguratorInput_InputValue_Scalar>,
	) {
		updateTreeItemModel(treeItemKey, (treeItemCurrent) => {
			const updatedComponents = updateComponentValues(treeItemCurrent.components, updatedComponentValuesByNames);
			const errors = getConfiguratorFormErrors(updatedComponents);

			return {
				...treeItemCurrent,
				components: updatedComponents,
				errors: errors,
			};
		});

		afterInputChanged();
	}

	async function transformTreeItemComponents({
		treeItemKey,
		event,
		recursive,
	}: {
		treeItemKey: string;
		event?: ConfiguratorEvent;
		recursive?: boolean;
	}) {
		await executeAsync(() => transformTreeItemComponentsWithoutLoading({ treeItemKey, event, recursive }));
	}

	async function transformTreeItemComponentsWithoutLoading({
		treeItemKey,
		event,
		recursive,
	}: {
		treeItemKey: string;
		event?: ConfiguratorEvent;
		recursive?: boolean;
	}) {
		await ensureTreeItemHasConfiguratorVersion(treeItemKey);
		const treeItem = getTreeItemWithParentProperties(rootItemRef.current, treeItemKey);
		if (treeItem == null) return;

		const transformResult = await PartConfiguratorFormApi.transformComponents({
			configurationSessionId: treeItem.configurationSessionId,
			configuratorType: treeItem.configuratorType,
			configuratorInput: getTreeItemConfiguratorInput(treeItem),
			event: event,
		});

		updateTreeItemModel(treeItemKey, (treeItemCurrent) => ({
			...treeItemCurrent,
			components: transformResult.components,
			componentsWithDocumentsIds: transformResult.componentsWithDocument,
			propertyValues: transformResult.propertyValues,
			label: transformResult.configuratorLabel,
			errors: getConfiguratorFormErrors(transformResult.components),
		}));

		if (recursive) {
			for (const child of treeItem.children) {
				await transformTreeItemComponentsWithoutLoading({
					treeItemKey: child.key,
					event: event,
					recursive: true,
				});
			}
		}
	}

	/**
	 * Refreshes propertyValues and label of the tree item with the given key.
	 */
	async function refreshTreeItemPropertyValues(treeItemKey: string) {
		await executeAsync(() => refreshTreeItemPropertyValuesWithoutLoading(treeItemKey, false));
	}

	async function refreshTreeItemPropertyValuesWithoutLoading(treeItemKey: string, resolveRecursively: boolean) {
		const treeItem = getTreeItemWithParentProperties(rootItemRef.current, treeItemKey);
		if (treeItem == null) return;

		const resolveStateResult = await PartConfiguratorFormApi.resolveConfiguratorState({
			configurationSessionId: treeItem.configurationSessionId,
			configuratorType: treeItem.configuratorType,
			configuratorInput:
				resolveRecursively ?
					getTreeItemRecursiveConfiguratorInput(treeItem)
				:	getTreeItemConfiguratorInput(treeItem),
			resolveRecursively: resolveRecursively,
		});
		updateTreeItemModel(treeItemKey, (currentState) => ({
			...currentState,
			propertyValues: resolveStateResult.propertyValues,
			label: resolveStateResult.label,
		}));
	}

	async function selectTreeItem(newSelectedItemKey: string, recursive = false) {
		const oldSelectedItemKey = selectedItemKey;
		if (oldSelectedItemKey === newSelectedItemKey) return;

		await refreshTreeItemPropertyValues(oldSelectedItemKey);
		if (!allowInputErrors && !readonly) {
			const selectedItemAncestorsHaveErrors = getAnyAncestorHasErrors(rootItemRef.current, newSelectedItemKey);
			if (selectedItemAncestorsHaveErrors) {
				showMessageDialog({
					title: i18n.t("fix_input_errors"),
					content: i18n.t("fix_input_errors_on_upper_levels_first"),
				});
				return "interrupted";
			}
		}

		setSelectedItemKey(newSelectedItemKey);
		await transformTreeItemComponents({ treeItemKey: newSelectedItemKey, recursive: recursive });
	}

	function toggleTreeItemExpansion(key: string) {
		updateTreeItemModel(key, (treeItemCurrent) => ({
			...treeItemCurrent,
			expanded: !treeItemCurrent.expanded,
		}));
	}

	async function onAddSubConfiguratorClicked(parentKey: string) {
		const parentModel = findTreeItem(rootItemRef.current, parentKey);
		if (parentModel == null) return;

		if (!allowInputErrors) {
			const anyAncestorHasErrors = getAnyAncestorHasErrors(rootItemRef.current, parentKey);
			const parentHasErrors = getConfiguratorFormErrors(parentModel.components).length > 0;
			if (anyAncestorHasErrors || parentHasErrors) {
				showMessageDialog({
					title: i18n.t("fix_input_errors"),
					content: i18n.t("fix_input_errors_on_upper_levels_first"),
				});
				return;
			}
		}

		openDialog(({ closeDialog }) => ({
			title: i18n.t("add_sub_configuration"),
			size: "lg",
			content: (
				<AddSubConfiguratorsToTreeView
					treeItem={parentModel}
					closeDialog={closeDialog}
					onSubmit={async (selectedComponents) => {
						await closeDialog();
						for (const selectedComponent of selectedComponents) {
							await addSubConfigurator(parentKey, selectedComponent);
						}
					}}
					addSubConfiguratorButtonSx={addSubConfiguratorButtonSx}
					addSubConfiguratorButtonContextMenu={addSubConfiguratorButtonContextMenu}
					subConfigurationImportAction={getSubConfigurationImportButtonAction(parentModel, closeDialog)}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.addSubConfiguration.dialog"),
				title: i18n.t("add_sub_configuration"),
				pathSegments: [parentModel.label],
				data: {
					...getPartConfiguratorAgentContextData(parentModel.configuratorType),
					treeItemKey: parentKey,
				},
			},
		}));
	}

	function getSubConfigurationImportButtonAction(
		targetParent: ConfiguratorTreeItemModel,
		closeAddDialog: () => Promise<void>,
	): SubConfigurationImportButtonAction | undefined {
		if (subConfigurationImportAction == null) return undefined;

		return {
			title: subConfigurationImportAction.title,
			onClick: async () => {
				await closeAddDialog();
				await subConfigurationImportAction.onClick({
					targetParent: targetParent,
					importSubConfigurations: async (assignments) => {
						for (const { sourceItem, targetComponent } of assignments) {
							await addSubConfigurator(
								targetParent.key,
								targetComponent,
								prepareImportedSubConfiguration({
									sourceItem: sourceItem,
									targetParent: targetParent,
									targetComponent: targetComponent,
								}),
							);
						}
					},
				});
			},
		};
	}

	async function addSubConfigurator(
		parentItemKey: string,
		subConfiguratorComponent:
			TransformedConfigurationComponent_SubConfigurator | TransformedConfigurationComponent_SubConfiguratorList,
		copySource: ConfiguratorTreeItemModel | undefined = undefined,
	) {
		const parentItem = findTreeItem(rootItemRef.current, parentItemKey);
		if (!parentItem) return;

		let newItem: ConfiguratorTreeItemModel;
		if (copySource != null) {
			newItem = regenerateKeysForAllTreeItems(copySource);
		} else {
			const subConfiguratorListIndex =
				subConfiguratorComponent.type === "subConfiguratorList" ?
					parentItem.children.filter(
						(child) => child.selfConfiguratorComponentName === subConfiguratorComponent.name,
					).length + 1
				:	null;
			const newSubConfiguratorType: SubConfiguratorPartConfiguratorType = {
				type: "subConfigurator",
				configuratorVersion: null,
				parentConfiguratorType: parentItem.configuratorType,
				parentProperties: parentItem.propertyValues,
				subConfiguratorCatalogPartId: subConfiguratorComponent.catalogPartId,
				subConfiguratorListIndex: subConfiguratorListIndex,
			};

			newSubConfiguratorType.configuratorVersion = await PartConfiguratorFormApi.resolveSubConfiguratorVersion({
				configuratorType: newSubConfiguratorType,
			});

			const { treeModel: newSubConfiguratorTreeModel } = await PartConfiguratorFormApi.getConfiguratorTreeModel({
				rootConfiguratorType: newSubConfiguratorType,
			});
			newItem = newSubConfiguratorTreeModel.root;
			newItem.selfConfiguratorComponentName = subConfiguratorComponent.name;
			// Clear parentProperties to reduce size of the tree item
			newItem.configuratorType = {
				...newSubConfiguratorType,
				parentProperties: { props: [] },
			};
		}

		updateTreeItemModel(parentItem.key, (parentItemCurrent) => {
			const updatedParentItem = addChildToTreeItem(parentItemCurrent, newItem);
			if (copySource == null) return updatedParentItem;

			return {
				...updatedParentItem,
				children: updatedParentItem.children.map((child) =>
					child.key === newItem.key ?
						synchronizeImportedSubConfigurationDescendantParentTypes(child)
					:	child,
				),
			};
		});

		await selectTreeItem(newItem.key, copySource != null);

		afterInputChanged();
	}

	async function deleteSubConfiguratorListItem(key: string) {
		const parentItem = findTreeItemParent(rootItemRef.current, key);
		if (parentItem == null) return;

		await executeAsync(async () => {
			const newChildren = parentItem.children.filter((child) => child.key !== key);
			const updatedParentItem = updateSubConfiguratorListIndexes({
				...parentItem,
				children: newChildren,
			});
			const reindexedChildKeys = getSubConfiguratorListIndexChangedChildKeys(parentItem, updatedParentItem);
			updateTreeItemModel(parentItem.key, () => updatedParentItem);

			afterInputChanged();
			await refreshTreeItemPropertyValuesWithoutLoading(parentItem.key, true);
			for (const reindexedChildKey of reindexedChildKeys) {
				await transformTreeItemComponentsWithoutLoading({
					treeItemKey: reindexedChildKey,
					recursive: true,
				});
			}
			await refreshTreeItemPropertyValuesWithoutLoading(parentItem.key, true);
		});
	}

	async function cloneSubConfiguratorListItem(treeItemKey: string) {
		const sourceItem = findTreeItem(rootItemRef.current, treeItemKey);
		if (sourceItem == null) return;

		const parentItem = findTreeItemParent(rootItemRef.current, treeItemKey);
		if (parentItem == null) return;

		const subConfiguratorComponents = getTreeItemSubConfiguratorComponents(parentItem);
		const subConfiguratorComponent = subConfiguratorComponents.find(
			(component) => component.name === sourceItem.selfConfiguratorComponentName,
		);
		if (subConfiguratorComponent == null) return;

		await addSubConfigurator(parentItem.key, subConfiguratorComponent, sourceItem);
	}

	/**
	 * Due to previous bug, some tree items may not have configuratorVersion set.
	 * This function ensures that the given tree item has a configuratorVersion.
	 */
	async function ensureTreeItemHasConfiguratorVersion(treeItemKey: string) {
		const treeItem = findTreeItem(rootItemRef.current, treeItemKey);
		if (treeItem == null) return;
		if (treeItem.configuratorType.type !== "subConfigurator") return;
		if (treeItem.configuratorType.configuratorVersion != null) return;

		const configuratorVersion = await PartConfiguratorFormApi.resolveSubConfiguratorVersion({
			configuratorType: treeItem.configuratorType,
		});
		updateTreeItemModel(treeItemKey, (currentState) => ({
			...currentState,
			configuratorType: {
				...currentState.configuratorType,
				configuratorVersion: configuratorVersion,
			},
		}));
	}

	function updateTreeItemModel(
		key: string,
		updateFn: (state: ConfiguratorTreeItemModel) => ConfiguratorTreeItemModel,
	) {
		const updatedRootItem = updateConfiguratorTreeItemState(rootItemRef.current, key, updateFn);
		setRootItem(updatedRootItem);
	}

	function afterInputChanged() {
		setHasUnsavedChanges(true);
		setConfiguratorInputVersion((prevVersion) => prevVersion + 1);
	}

	async function onSubmitForm() {
		if (!allowInputErrors) {
			const errors = updateAndShowPossibleErrors();
			if (errors != undefined) return;
		}

		const finalConfiguratorInput = getTreeItemRecursiveConfiguratorInput(rootItemRef.current);
		onSubmit?.({ type: "success", value: finalConfiguratorInput });

		if (onCompleted != null) {
			const finalRootState = await PartConfiguratorFormApi.resolveConfiguratorState({
				configurationSessionId: rootItemRef.current.configurationSessionId,
				configuratorType: rootItemRef.current.configuratorType,
				configuratorInput: finalConfiguratorInput,
				resolveRecursively: true,
			});

			const completionResult = await onCompleted({
				type: "success",
				value: {
					propertyValues: finalRootState.propertyValues,
					configuratorInput: finalConfiguratorInput,
					configurationSessionId: rootItemRef.current.configurationSessionId,
					checkpointState: checkpointState,
				},
			});
			if (completionResult === PART_CONFIGURATION_NOT_SAVED) return;
		}

		await storeConfiguratorTreeModelToServer({
			storageKey: treeModelStorageKey,
			rootItem: rootItemRef.current,
			hasUnsavedChanges: false,
		});
		setHasUnsavedChanges(false);
	}

	async function onCancelForm() {
		await onSubmit?.({
			type: "cancel",
			isEdited: hasUnsavedChanges,
		});
		await onCompleted?.({
			type: "cancel",
			isEdited: hasUnsavedChanges,
		});
	}

	async function onValidateConfiguration() {
		const errors = updateAndShowPossibleErrors();
		if (errors != undefined) return;

		const validationResult = await PartConfiguratorFormApi.validateConfiguration({
			configurationSessionId: rootItemRef.current.configurationSessionId,
			configuratorInput: getTreeItemRecursiveConfiguratorInput(rootItemRef.current),
			configuratorType: rootItemRef.current.configuratorType,
		});

		switch (validationResult.type) {
			case "SUCCESS":
				showMessageDialog({
					title: i18n.t("validation_success"),
					content: i18n.t("part_configuration_validation_success_message"),
				});
				break;
			case "FAILURE":
				showMessageDialog({
					title: i18n.t("validation_error"),
					content: (
						<VerticalBox sx={{ padding: 2 }}>
							{validationResult.problems.map((message, index) => (
								<Typography key={index} gutterBottom>
									{message}
								</Typography>
							))}
						</VerticalBox>
					),
				});
				break;
		}
	}

	async function resetForm(newModelSource: ConfiguratorTreeModelSource) {
		await executeAsync(async () => {
			await clearStoredConfiguratorTreeModel({
				configuratorType: rootItemRef.current.configuratorType,
				environmentType: environmentType,
			});
			const { treeModel: newTreeModel } = await PartConfiguratorFormApi.getConfiguratorTreeModel({
				rootConfiguratorType: rootItemRef.current.configuratorType,
				source: newModelSource,
			});
			const newRootItemState = newTreeModel.root;

			setRootItem(newRootItemState);
			setSelectedItemKey(newRootItemState.key);
			setHasUnsavedChanges(newTreeModel.hasUnsavedChanges);
			setConfiguratorInputVersion((prevVersion) => prevVersion + 1);
			setCheckpointState(defaultCheckpointState);
		});
	}

	async function updateAllVersions() {
		await executeAsync(async () => {
			const updatedTreeItem = await PartConfiguratorFormApi.updateConfiguratorVersions({
				treeItem: rootItemRef.current,
			});
			setRootItem(updatedTreeItem);
			afterInputChanged();

			// Resolve and refresh all configurators after version update
			await transformTreeItemComponents({
				treeItemKey: rootItemRef.current.key,
				recursive: true,
			});
		});
	}

	async function updateSingleSubConfiguratorVersion(treeItemKey: string) {
		await executeAsync(async () => {
			const treeItem = findTreeItem(rootItemRef.current, treeItemKey);
			if (treeItem == null) return;
			if (treeItem.configuratorType.type !== "subConfigurator") return;

			const latestVersion = await PartConfiguratorFormApi.resolveSubConfiguratorVersion({
				configuratorType: {
					...treeItem.configuratorType,
					configuratorVersion: null,
				},
			});

			updateTreeItemModel(treeItemKey, (currentState) => ({
				...currentState,
				configuratorType: {
					...currentState.configuratorType,
					configuratorVersion: latestVersion,
				},
			}));
			afterInputChanged();

			await transformTreeItemComponents({
				treeItemKey: treeItemKey,
				recursive: true,
			});
		});
	}

	function updateAndShowPossibleErrors(): ConfiguratorTreeItemInputErrors | undefined {
		const errors = getConfiguratorTreeRecursiveInputErrors(rootItemRef.current);
		if (errors != undefined) {
			openDialog({
				title: i18n.t("configurator_form_errors_title"),
				size: "sm",
				content: <ConfiguratorTreeErrorsView errors={errors} />,
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.configuratorFormErrors.dialog"),
					title: i18n.t("configurator_form_errors_title"),
					data: getPartConfiguratorAgentContextData(rootItemRef.current.configuratorType),
				},
			});
		}
		return errors;
	}

	async function executeAsync<T>(operation: () => Promise<T>) {
		setIsLoading(true);
		setServerError(undefined);
		try {
			await operation();
		} catch (e) {
			const loggedError = logError(e);
			setServerError(loggedError);
		} finally {
			setIsLoading(false);
		}
	}
};
